import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { ArrowLeft, CalendarClock, KeyRound, Pencil, Trash2, UserCheck, UserX, Plus, Clock } from 'lucide-react';
import { offsetLabel, todayIn, type EmployeeDetail, type RoleKey } from '@workforce/shared';
import { formatMinutes, t } from '@/lib/i18n';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useCalendars, useSchedules } from '@/lib/queries';
import { useTz } from '@/lib/timezone';
import { country, describeAction, employmentStatus, employmentType, flag, fmtDate, workArrangement } from '@/lib/format';
import { TimeZoneSelect } from '@/components/TimeZoneSelect';
import {
  Avatar,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DescList,
  EmptyState,
  ErrorBanner,
  Field,
  Input,
  Modal,
  Notice,
  Select,
  Spinner,
  Tabs,
  Textarea,
  Toggle,
  statusTone,
} from '@/components/ui';
import { TempPassword } from './EmployeeForm';
import { useLeavePolicies } from '@/lib/leave';
import { BalanceCards } from '@/components/BalanceCards';
import { LeaveRequestList } from '@/components/LeaveRequestList';
import { SalaryTab } from '@/components/SalaryTab';
import { EmployeeBalanceModal } from './Balances';
import type { LeaveBalanceDto, LeaveRequestDto } from '@workforce/shared';

type Tab = 'overview' | 'leave' | 'terms' | 'salary' | 'account' | 'activity';

function LocalClock({ zone }: { zone: string }) {
  const [now, setNow] = useState(() => DateTime.now().setZone(zone));
  useEffect(() => {
    const timer = setInterval(() => setNow(DateTime.now().setZone(zone)), 30_000);
    return () => clearInterval(timer);
  }, [zone]);
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-slate-500">
      <Clock className="h-4 w-4" />
      {t('Local time {time} · {zone} ({offset})', { time: now.toFormat('HH:mm, ccc dd LLL'), zone, offset: offsetLabel(zone) })}
    </span>
  );
}

export function EmployeeDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { can } = useAuth();
  const isAdmin = can('employees.write');
  const [tab, setTab] = useState<Tab>(() => (new URLSearchParams(window.location.search).get('tab') as Tab | null) ?? 'overview');
  const [dialog, setDialog] = useState<null | 'deactivate' | 'reactivate' | 'delete'>(null);
  const [endDate, setEndDate] = useState(DateTime.now().toISODate()!);

  const q = useQuery({ queryKey: ['employee', id], queryFn: () => api.get<EmployeeDetail>(`/employees/${id}`) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['employee', id] });
    void qc.invalidateQueries({ queryKey: ['employees'] });
  };

  const lifecycle = useMutation({
    mutationFn: ({ kind, reason }: { kind: 'deactivate' | 'reactivate' | 'delete'; reason: string }) =>
      kind === 'delete'
        ? api.del(`/employees/${id}`, { reason })
        : kind === 'deactivate'
          ? api.post(`/employees/${id}/deactivate`, { endDate, reason })
          : api.post(`/employees/${id}/reactivate`, { reason }),
    onSuccess: (_d, v) => {
      setDialog(null);
      if (v.kind === 'delete') {
        void qc.invalidateQueries({ queryKey: ['employees'] });
        nav('/employees');
      } else refresh();
    },
  });

  if (q.isLoading) return <Spinner />;
  if (q.error)
    return (
      <div>
        <ErrorBanner error={q.error} />
        <Link to="/employees" className="text-sm text-brand-600">
          {t('Back to employees')}
        </Link>
      </div>
    );
  const e = q.data!;
  const tabs: { value: Tab; label: string }[] = [
    { value: 'overview', label: t('Overview') },
    ...(can('leave.read') ? [{ value: 'leave' as Tab, label: t('Leave') }] : []),
    { value: 'terms', label: t('Schedule & time zone') },
    ...(can('payroll.manage') ? [{ value: 'salary' as Tab, label: t('Salary') }] : []),
    ...(isAdmin ? [{ value: 'account' as Tab, label: t('Login account') }] : []),
    ...(can('audit.read') ? [{ value: 'activity' as Tab, label: t('Activity') }] : []),
  ];

  return (
    <div>
      <Link to="/employees" className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> {t('Employees')}
      </Link>

      <div className="card mb-6 p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <Avatar name={e.fullName} size="lg" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold text-slate-900">{e.fullName}</h1>
              <Badge tone={statusTone(e.employmentStatus)}>{t(employmentStatus(e.employmentStatus))}</Badge>
              {e.account && <Badge tone="brand">{t(e.account.role)}</Badge>}
              {e.workArrangement !== 'office' && <Badge tone="violet">{t(workArrangement(e.workArrangement))}</Badge>}
            </div>
            <p className="mt-0.5 text-sm text-slate-600">
              {e.position ?? t('No position')} {e.departmentName ? `· ${e.departmentName}` : ''} · {e.employeeCode}
            </p>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
              <span className="text-sm text-slate-500">
                {flag(e.countryCode)} {e.city ? `${e.city}, ` : ''}
                {country(e.countryCode)}
              </span>
              {e.timezone && <LocalClock zone={e.timezone} />}
              {can('attendance.read') && (
                <Link to={`/attendance?tab=employee&employee=${e.id}`} className="text-sm font-medium text-brand-700 hover:underline">
                  {t('Attendance →')}
                </Link>
              )}
            </div>
          </div>
          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" icon={<Pencil className="h-4 w-4" />} onClick={() => nav(`/employees/${e.id}/edit`)}>
                {t('Edit')}
              </Button>
              {e.isActive ? (
                <Button variant="secondary" icon={<UserX className="h-4 w-4" />} onClick={() => setDialog('deactivate')}>
                  {t('Deactivate')}
                </Button>
              ) : (
                <Button variant="secondary" icon={<UserCheck className="h-4 w-4" />} onClick={() => setDialog('reactivate')}>
                  {t('Reactivate')}
                </Button>
              )}
              <Button variant="ghost" icon={<Trash2 className="h-4 w-4" />} onClick={() => setDialog('delete')} aria-label={t('Delete')} />
            </div>
          )}
        </div>
      </div>

      {!e.isActive && (
        <div className="mb-4">
          <Notice tone="amber">
            {t('Deactivated on {date} (last working day {endDate}). Their login is disabled; history is kept.', {
              date: fmtDate(e.deactivatedAt?.slice(0, 10)),
              endDate: fmtDate(e.endDate),
            })}
          </Notice>
        </div>
      )}

      <Tabs tabs={tabs} value={tab} onChange={setTab} />
      {tab === 'overview' && <Overview e={e} />}
      {tab === 'leave' && <EmployeeLeaveTab employeeId={e.id} employeeName={e.fullName} />}
      {tab === 'terms' && <TermsTab e={e} isAdmin={isAdmin} onChange={refresh} />}
      {tab === 'salary' && can('payroll.manage') && <SalaryTab employeeId={e.id} employeeName={e.fullName} />}
      {tab === 'account' && isAdmin && <AccountTab e={e} onChange={refresh} />}
      {tab === 'activity' && <ActivityTab id={e.id} />}

      <ConfirmDialog
        open={dialog === 'deactivate'}
        onClose={() => setDialog(null)}
        title={t('Deactivate {name}?', { name: e.fullName })}
        message={t('The employee is marked as terminated, their login is disabled and all their sessions end immediately. All history is kept, and they can be reactivated later.')}
        confirmLabel={t('Deactivate')}
        danger
        requireReason
        loading={lifecycle.isPending}
        error={lifecycle.error}
        onConfirm={(reason) => lifecycle.mutate({ kind: 'deactivate', reason })}
      >
        <Field label={t('Last working day')} required>
          <Input type="date" value={endDate} onChange={(ev) => setEndDate(ev.target.value)} />
        </Field>
      </ConfirmDialog>
      <ConfirmDialog
        open={dialog === 'reactivate'}
        onClose={() => setDialog(null)}
        title={t('Reactivate {name}?', { name: e.fullName })}
        message={t('The employee becomes active again. Their login stays disabled until you enable it on the Login account tab.')}
        confirmLabel={t('Reactivate')}
        requireReason
        loading={lifecycle.isPending}
        error={lifecycle.error}
        onConfirm={(reason) => lifecycle.mutate({ kind: 'reactivate', reason })}
      />
      <ConfirmDialog
        open={dialog === 'delete'}
        onClose={() => setDialog(null)}
        title={t('Permanently delete {name}?', { name: e.fullName })}
        message={t('Only use this for records created by mistake. Employees with a login account, reports or any history cannot be deleted — deactivate them instead.')}
        confirmLabel={t('Delete permanently')}
        danger
        requireReason
        loading={lifecycle.isPending}
        error={lifecycle.error}
        onConfirm={(reason) => lifecycle.mutate({ kind: 'delete', reason })}
      />
    </div>
  );
}

function Overview({ e }: { e: EmployeeDetail }) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <Card title={t('Profile')} className="lg:col-span-2">
        <DescList
          items={[
            { label: t('E-mail'), value: <a href={`mailto:${e.email}`} className="text-brand-700 hover:underline">{e.email}</a> },
            { label: t('Phone'), value: e.phone ?? '—' },
            { label: t('Country'), value: `${flag(e.countryCode)} ${country(e.countryCode)}` },
            { label: t('City'), value: e.city ?? '—' },
            { label: t('Department'), value: e.departmentName ?? '—' },
            { label: t('Position'), value: e.position ?? '—' },
            { label: t('Manager'), value: e.managerId ? <Link className="text-brand-700 hover:underline" to={`/employees/${e.managerId}`}>{e.managerName}</Link> : '—' },
            { label: t('Employment type'), value: t(employmentType(e.employmentType)) },
            { label: t('Work arrangement'), value: `${t(workArrangement(e.workArrangement))}${e.officeName ? ` · ${e.officeName}` : ''}` },
            { label: t('Start date'), value: fmtDate(e.startDate) },
            { label: t('End date'), value: fmtDate(e.endDate) },
            { label: t('Can approve leave'), value: e.canApproveLeave ? t('Yes (for their team)') : t('No') },
            { label: t('Time zone'), value: e.timezone ?? '—' },
            ...(e.leaveFacts
              ? [
                  { label: t('Date of birth'), value: fmtDate(e.leaveFacts.dateOfBirth) },
                  { label: t('Years of experience before joining'), value: String(e.leaveFacts.priorExperienceYears) },
                  { label: t('Has a disability'), value: e.leaveFacts.hasDisability ? t('Yes') : t('No') },
                ]
              : []),
          ]}
        />
      </Card>
      <div className="space-y-6">
        <Card title={t('Work schedule')}>
          {e.currentTerms ? (
            <div className="space-y-1 text-sm">
              <p className="font-medium text-slate-900">{e.currentTerms.workScheduleName}</p>
              <p className="text-slate-500">{t('Holidays: {name}', { name: e.currentTerms.holidayCalendarName ?? t('None') })}</p>
              <p className="text-slate-500">{t('Since {date}', { date: fmtDate(e.currentTerms.effectiveFrom) })}</p>
            </div>
          ) : (
            <p className="text-sm text-slate-500">{t('No terms in effect yet.')}</p>
          )}
        </Card>
        <Card title={t('Direct reports ({n})', { n: e.directReports.length })}>
          {e.directReports.length ? (
            <ul className="space-y-2">
              {e.directReports.map((r) => (
                <li key={r.id}>
                  <Link to={`/employees/${r.id}`} className="flex items-center gap-2 text-sm hover:text-brand-700">
                    <Avatar name={r.fullName} size="sm" /> {r.fullName}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">{t('None')}</p>
          )}
        </Card>
        <Card title={t('Projects ({n})', { n: e.projects.length })}>
          {e.projects.length ? (
            <ul className="space-y-2">
              {e.projects.map((p) => (
                <li key={p.projectId} className="text-sm">
                  <Link to={`/projects/${p.projectId}`} className="font-medium text-slate-900 hover:text-brand-700">
                    {p.name}
                    {p.code && <span className="ms-1 text-xs text-slate-400">({p.code})</span>}
                  </Link>
                  <p className="text-xs text-slate-500">
                    {p.role ? `${p.role} · ` : ''}
                    {t('Since {date}', { date: fmtDate(p.fromDate) })}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">{t('Not on any project')}</p>
          )}
        </Card>
        <Card title={t('Leave policy')}>
          <p className="text-sm text-slate-900">{e.currentTerms?.leavePolicyName ?? t('None assigned')}</p>
          <p className="mt-1 text-xs text-slate-500">{t('Balances and requests are on the Leave tab.')}</p>
        </Card>
      </div>
    </div>
  );
}

function TermsTab({ e, isAdmin, onChange }: { e: EmployeeDetail; isAdmin: boolean; onChange: () => void }) {
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  const schedules = useSchedules();
  const calendars = useCalendars();
  const tz = e.currentTerms?.timezone ?? e.timezone ?? 'UTC';
  const today = todayIn(tz);
  const policies = useLeavePolicies();
  const [form, setForm] = useState({ effectiveFrom: today, timezone: '', workScheduleId: '', holidayCalendarId: '', leavePolicyId: '', reason: '' });
  useEffect(() => {
    if (open && e.currentTerms)
      setForm({
        effectiveFrom: DateTime.fromISO(today).plus({ days: 1 }).toISODate()!,
        timezone: e.currentTerms.timezone,
        workScheduleId: e.currentTerms.workScheduleId,
        holidayCalendarId: e.currentTerms.holidayCalendarId ?? '',
        leavePolicyId: e.currentTerms.leavePolicyId ?? '',
        reason: '',
      });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = useMutation({
    mutationFn: () =>
      api.post(`/employees/${e.id}/work-terms`, {
        effectiveFrom: form.effectiveFrom,
        timezone: form.timezone,
        workScheduleId: form.workScheduleId,
        holidayCalendarId: form.holidayCalendarId || null,
        leavePolicyId: form.leavePolicyId || null,
        ...(form.reason ? { reason: form.reason } : {}),
      }),
    onSuccess: () => {
      setOpen(false);
      onChange();
    },
  });
  const del = useMutation({
    mutationFn: (termsId: string) => api.del(`/employees/${e.id}/work-terms/${termsId}`),
    onSuccess: () => {
      setToDelete(null);
      onChange();
    },
  });
  const retro = form.effectiveFrom < today;
  const fe = add.error instanceof ApiError ? add.error.fieldErrors() : {};
  const sched = schedules.data?.find((s) => s.id === e.currentTerms?.workScheduleId);

  return (
    <div className="space-y-6">
      <Card
        title={t('Terms history')}
        actions={
          isAdmin && (
            <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => setOpen(true)}>
              {t('Schedule a change')}
            </Button>
          )
        }
        bodyClass="p-0"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50">
              <tr>
                <th className="th">{t('Effective from')}</th>
                <th className="th">{t('Time zone')}</th>
                <th className="th">{t('Work schedule')}</th>
                <th className="th">{t('Holiday calendar')}</th>
                <th className="th">{t('Leave policy')}</th>
                <th className="th">{t('Recorded')}</th>
                <th className="th" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {e.termsHistory.map((term) => {
                const isCurrent = e.currentTerms?.id === term.id;
                const isFuture = term.effectiveFrom > todayIn(term.timezone);
                return (
                  <tr key={term.id} className={isCurrent ? 'bg-brand-50/40' : ''}>
                    <td className="td">
                      <span className="font-medium">{fmtDate(term.effectiveFrom)}</span>{' '}
                      {isCurrent && <Badge tone="green">{t('Current')}</Badge>}
                      {isFuture && <Badge tone="violet">{t('Scheduled')}</Badge>}
                    </td>
                    <td className="td">{term.timezone}</td>
                    <td className="td">{term.workScheduleName}</td>
                    <td className="td">{term.holidayCalendarName ?? '—'}</td>
                    <td className="td">{term.leavePolicyName ?? '—'}</td>
                    <td className="td text-xs text-slate-500">
                      {fmtDate(term.createdAt.slice(0, 10))} {term.createdBy ? t('by {name}', { name: term.createdBy }) : ''}
                    </td>
                    <td className="td text-end">
                      {isAdmin && isFuture && (
                        <Button variant="ghost" size="sm" onClick={() => setToDelete(term.id)}>
                          {t('Remove')}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {sched && (
        <Card title={t('Current schedule: {name}', { name: sched.name })}>
          <WeekView days={sched.days} />
        </Card>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('Schedule a work-terms change')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button loading={add.isPending} onClick={() => add.mutate()} disabled={!form.timezone || !form.workScheduleId || (retro && form.reason.trim().length < 3)}>
              {t('Save change')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <ErrorBanner error={add.error && !Object.keys(fe).length ? add.error : null} />
          <Notice>
            {t('A change never edits past days: it applies from the effective date onward. Use a future date for planned changes (e.g. relocation).')}
          </Notice>
          <Field label={t('Effective from')} required error={fe.effectiveFrom}>
            <Input type="date" value={form.effectiveFrom} onChange={(ev) => setForm({ ...form, effectiveFrom: ev.target.value })} />
          </Field>
          {retro && <Notice tone="amber">{t('This date is in the past. A reason is required and the change is flagged as retroactive in the audit log.')}</Notice>}
          <Field label={t('Time zone')} required error={fe.timezone}>
            <TimeZoneSelect value={form.timezone} onChange={(v) => setForm({ ...form, timezone: v })} />
          </Field>
          <Field label={t('Work schedule')} required error={fe.workScheduleId}>
            <Select value={form.workScheduleId} onChange={(ev) => setForm({ ...form, workScheduleId: ev.target.value })}>
              {schedules.data?.filter((s) => s.isActive).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('Holiday calendar')}>
            <Select value={form.holidayCalendarId} onChange={(ev) => setForm({ ...form, holidayCalendarId: ev.target.value })}>
              <option value="">{t('None')}</option>
              {calendars.data?.filter((c) => c.isActive).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('Leave policy')} hint={t('Applies to leave years created after this date; existing balances are not changed.')}>
            <Select value={form.leavePolicyId} onChange={(ev) => setForm({ ...form, leavePolicyId: ev.target.value })}>
              <option value="">{t('None')}</option>
              {policies.data?.filter((p) => p.isActive || p.id === form.leavePolicyId).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={retro ? t('Reason') : t('Reason (optional)')} required={retro} error={fe.reason}>
            <Textarea rows={2} value={form.reason} onChange={(ev) => setForm({ ...form, reason: ev.target.value })} />
          </Field>
        </div>
      </Modal>
      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        title={t('Remove scheduled change?')}
        message={t('This change has not taken effect yet, so removing it does not affect any past records.')}
        confirmLabel={t('Remove')}
        danger
        loading={del.isPending}
        error={del.error}
        onConfirm={() => toDelete && del.mutate(toDelete)}
      />
    </div>
  );
}

export function WeekView({ days }: { days: { weekday: number; startTime: string; endTime: string; breakMinutes: number; scheduledMinutes: number }[] }) {
  const names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7">
      {names.map((n, i) => {
        const d = days.find((x) => x.weekday === i + 1);
        return (
          <div key={n} className={`rounded-lg border p-3 text-center ${d ? 'border-brand-200 bg-brand-50/50' : 'border-slate-200 bg-slate-50'}`}>
            <p className="text-xs font-semibold uppercase text-slate-500">{t(n)}</p>
            {d ? (
              <>
                <p className="mt-1 whitespace-nowrap text-sm font-medium text-slate-900">
                  {d.startTime}–{d.endTime}
                </p>
                <p className="text-xs text-slate-500">
                  {formatMinutes(d.scheduledMinutes)}
                  {d.breakMinutes ? ` · ${t('{n}m break', { n: d.breakMinutes })}` : ''}
                </p>
              </>
            ) : (
              <p className="mt-1 text-sm text-slate-400">{t('Off')}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AccountTab({ e, onChange }: { e: EmployeeDetail; onChange: () => void }) {
  const canPayroll = useAuth().can('payroll.manage');
  const [role, setRole] = useState<RoleKey>(e.account?.role ?? 'employee');
  const [temp, setTemp] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<null | 'reset' | 'disable'>(null);
  const { fmt } = useTz();

  const create = useMutation({
    mutationFn: () => api.post<{ temporaryPassword: string }>(`/employees/${e.id}/account`, { role }),
    onSuccess: (r) => {
      setTemp(r.temporaryPassword);
      onChange();
    },
  });
  const update = useMutation({
    mutationFn: (body: { role?: RoleKey; isActive?: boolean; payrollAccess?: boolean; reason?: string }) => api.patch(`/employees/${e.id}/account`, body),
    onSuccess: () => {
      setConfirm(null);
      onChange();
    },
  });
  const reset = useMutation({
    mutationFn: () => api.post<{ temporaryPassword: string }>(`/employees/${e.id}/account/reset-password`, {}),
    onSuccess: (r) => {
      setConfirm(null);
      setTemp(r.temporaryPassword);
      onChange();
    },
  });

  if (!e.account) {
    return (
      <Card>
        <EmptyState
          icon={<KeyRound className="h-6 w-6" />}
          title={t('No login account')}
          description={e.isActive ? t('Create a login so this employee can sign in, see their leave and attendance.') : t('Reactivate the employee first.')}
          action={
            e.isActive && (
              <div className="flex items-end gap-2">
                <Field label={t('Role')}>
                  <Select value={role} onChange={(ev) => setRole(ev.target.value as RoleKey)}>
                    <option value="employee">{t('Employee')}</option>
                    <option value="manager">{t('Manager')}</option>
                    <option value="admin">{t('Administrator')}</option>
                  </Select>
                </Field>
                <Button loading={create.isPending} onClick={() => create.mutate()}>
                  {t('Create login')}
                </Button>
              </div>
            )
          }
        />
        <ErrorBanner error={create.error} />
        <Modal open={!!temp} onClose={() => setTemp(null)} title={t('Login created')} size="sm" footer={<Button onClick={() => setTemp(null)}>{t('Done')}</Button>}>
          {temp && <TempPassword name={e.fullName} password={temp} />}
        </Modal>
      </Card>
    );
  }

  const a = e.account;
  const locked = a.lockedUntil && new Date(a.lockedUntil) > new Date();
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card title={t('Account')}>
        <ErrorBanner error={update.error ?? reset.error} />
        <DescList
          items={[
            { label: t('Sign-in e-mail'), value: e.email },
            { label: t('Status'), value: a.isActive ? <Badge tone="green">{t('Enabled')}</Badge> : <Badge tone="slate">{t('Disabled')}</Badge> },
            { label: t('Last sign-in'), value: a.lastLoginAt ? fmt(a.lastLoginAt, e.timezone) : t('Never') },
            { label: t('Locked'), value: locked ? t('Until {time}', { time: fmt(a.lockedUntil, e.timezone, 'HH:mm') }) : t('No') },
            { label: t('Must change password'), value: a.mustChangePassword ? t('Yes') : t('No') },
          ]}
        />
        <div className="mt-5 flex flex-wrap gap-2">
          <Button variant="secondary" icon={<KeyRound className="h-4 w-4" />} onClick={() => setConfirm('reset')}>
            {t('Reset password')}
          </Button>
          {a.isActive ? (
            <Button variant="secondary" onClick={() => setConfirm('disable')}>
              {t('Disable login')}
            </Button>
          ) : (
            <Button variant="secondary" loading={update.isPending} onClick={() => update.mutate({ isActive: true })} disabled={!e.isActive}>
              {t('Enable login')}
            </Button>
          )}
        </div>
      </Card>
      <Card title={t('Role')}>
        <div className="flex items-end gap-2">
          <Field label={t('Role')} className="flex-1">
            <Select value={role} onChange={(ev) => setRole(ev.target.value as RoleKey)}>
              <option value="employee">{t('Employee — own data only')}</option>
              <option value="manager">{t('Manager — their team')}</option>
              <option value="admin">{t('Administrator — everything')}</option>
            </Select>
          </Field>
          <Button disabled={role === a.role} loading={update.isPending} onClick={() => update.mutate({ role })}>
            {t('Save role')}
          </Button>
        </div>
        <p className="mt-3 text-xs text-slate-500">{t('Changing the role signs the user out so their new permissions apply immediately.')}</p>
        {a.role === 'admin' && canPayroll && (
          <div className="mt-5 border-t border-slate-100 pt-4">
            <Toggle
              checked={a.payrollAccess}
              disabled={update.isPending}
              onChange={(v) => update.mutate({ payrollAccess: v })}
              label={t('Can see salaries & payroll')}
              description={t('Turn off to give this administrator everything except salaries (Payroll page, Salary tab and salary figures in the audit log).')}
            />
          </div>
        )}
      </Card>

      <Modal open={!!temp} onClose={() => setTemp(null)} title={t('New temporary password')} size="sm" footer={<Button onClick={() => setTemp(null)}>{t('Done')}</Button>}>
        {temp && <TempPassword name={e.fullName} password={temp} />}
      </Modal>
      <ConfirmDialog
        open={confirm === 'reset'}
        onClose={() => setConfirm(null)}
        title={t('Reset password?')}
        message={t('A new temporary password is generated, the user is signed out everywhere and must choose a new password at next sign-in.')}
        confirmLabel={t('Reset password')}
        loading={reset.isPending}
        onConfirm={() => reset.mutate()}
      />
      <ConfirmDialog
        open={confirm === 'disable'}
        onClose={() => setConfirm(null)}
        title={t('Disable login?')}
        message={t('The user is signed out immediately and cannot sign in until the login is enabled again.')}
        confirmLabel={t('Disable')}
        danger
        requireReason
        loading={update.isPending}
        error={update.error}
        onConfirm={(reason) => update.mutate({ isActive: false, reason })}
      />
    </div>
  );
}

function ActivityTab({ id }: { id: string }) {
  const { fmt } = useTz();
  const q = useQuery({
    queryKey: ['employee', id, 'audit'],
    queryFn: () =>
      api.get<{ id: string; occurredAt: string; actorLabel: string; action: string; oldValue: unknown; newValue: unknown; reason: string | null }[]>(
        `/employees/${id}/audit`,
      ),
  });
  if (q.isLoading) return <Spinner />;
  return (
    <Card bodyClass="p-0">
      {!q.data?.length ? (
        <EmptyState icon={<CalendarClock className="h-6 w-6" />} title={t('No activity yet')} />
      ) : (
        <ul className="divide-y divide-slate-100">
          {q.data.map((r) => (
            <li key={r.id} className="px-5 py-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-medium text-slate-800">{t(describeAction(r.action))}</p>
                <p className="text-xs text-slate-500">
                  {fmt(r.occurredAt, null)} · {r.actorLabel}
                </p>
              </div>
              <ChangeSummary oldValue={r.oldValue} newValue={r.newValue} />
              {r.reason && <p className="mt-1 text-xs text-slate-600">{t('Reason: {reason}', { reason: r.reason })}</p>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** Readable English names for field keys stored in audit records (translated where rendered). */
const FIELD_LABELS: Record<string, string> = {
  employeeCode: 'Employee ID',
  fullName: 'Full name',
  email: 'E-mail',
  phone: 'Phone',
  countryCode: 'Country',
  city: 'City',
  departmentId: 'Department',
  departmentName: 'Department',
  position: 'Position',
  managerId: 'Manager',
  managerName: 'Manager',
  employmentType: 'Employment type',
  employmentStatus: 'Employment status',
  startDate: 'Start date',
  endDate: 'End date',
  canApproveLeave: 'Can approve leave',
  workArrangement: 'Work arrangement',
  officeName: 'Office',
  attendancePin: 'Fingerprint machine ID',
  attendanceRequired: 'Working hours are tracked',
  attendanceCalcMode: 'Daily worked-time rule',
  hoursSource: 'Hours come from',
  trackWhen: 'The desktop agent records',
  effectiveFrom: 'Effective from',
  timezone: 'Time zone',
  workScheduleId: 'Work schedule',
  holidayCalendarId: 'Holiday calendar',
  leavePolicyId: 'Leave policy',
  workTerms: 'Work terms',
  accountDisabled: 'Login disabled',
  role: 'Role',
  isActive: 'Active',
  payrollAccess: 'Can see salaries & payroll',
  mustChangePassword: 'Must change password',
  note: 'Note',
  reason: 'Reason',
  name: 'Name',
  description: 'Description',
  date: 'Date',
  isHalfDay: 'Half day',
  year: 'Year',
  added: 'Added',
  removed: 'Removed',
  lateGraceMinutes: 'Late arrival grace (minutes)',
  earlyLeaveGraceMinutes: 'Early departure grace (minutes)',
  days: 'Working days',
  salaryAmount: 'Salary',
  salaryCurrency: 'Currency',
  amount: 'Amount',
  period: 'Period',
  status: 'Status',
};
const fieldLabel = (k: string) => FIELD_LABELS[k] ?? k;

export function ChangeSummary({ oldValue, newValue }: { oldValue: unknown; newValue: unknown }) {
  if (!oldValue && !newValue) return null;
  const hidden = (v: unknown) => !!v && typeof v === 'object' && (v as { hidden?: string }).hidden === 'payroll';
  if (hidden(oldValue) || hidden(newValue)) return <p className="mt-1 text-xs italic text-slate-400">{t('Salary details hidden (no payroll access)')}</p>;
  const o = (oldValue && typeof oldValue === 'object' ? oldValue : {}) as Record<string, unknown>;
  const n = (newValue && typeof newValue === 'object' ? newValue : {}) as Record<string, unknown>;
  const keys = [...new Set([...Object.keys(o), ...Object.keys(n)])].slice(0, 12);
  if (!keys.length) {
    return (
      <p className="mt-1 font-mono text-xs text-slate-500">
        {JSON.stringify(oldValue)} → {JSON.stringify(newValue)}
      </p>
    );
  }
  const show = (v: unknown) => (v === null || v === undefined || v === '' ? '∅' : typeof v === 'object' ? JSON.stringify(v) : String(v));
  return (
    <div className="mt-1 space-y-0.5">
      {keys.map((k) => (
        <p key={k} className="break-all text-xs text-slate-600">
          <span className="font-medium text-slate-700">{t(fieldLabel(k))}</span>:{' '}
          {k in o && <span className="text-red-600 line-through decoration-red-300">{show(o[k])}</span>}
          {k in o && k in n && ' → '}
          {k in n && <span className="text-emerald-700">{show(n[k])}</span>}
        </p>
      ))}
    </div>
  );
}

function EmployeeLeaveTab({ employeeId, employeeName }: { employeeId: string; employeeName: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['leave', 'employee', employeeId],
    queryFn: () => api.get<{ balances: LeaveBalanceDto[]; requests: LeaveRequestDto[] }>(`/leave/employees/${employeeId}`),
  });
  if (q.isLoading) return <Spinner />;
  if (!q.data) return <ErrorBanner error={q.error} />;
  return (
    <div className="space-y-6">
      <BalanceCards balances={q.data.balances} onSelect={(b) => setOpen(b.leaveTypeId)} />
      <Card title={t('Leave requests')} bodyClass="p-0">
        <LeaveRequestList items={q.data.requests} showEmployee={false} onChanged={() => void qc.invalidateQueries({ queryKey: ['leave'] })} />
      </Card>
      {open && (
        <EmployeeBalanceModal
          row={{ employeeId, employeeName }}
          date={DateTime.now().toISODate()!}
          initialTypeId={open}
          onClose={() => {
            setOpen(null);
            void qc.invalidateQueries({ queryKey: ['leave', 'employee', employeeId] });
          }}
        />
      )}
    </div>
  );
}
